import { useCallback, useEffect, useState } from 'react'
import { BookingShell, CenteredState, type SummaryRow } from '../components/BookingShell'
import {
  BookingApiError,
  cancelAppointment,
  fetchAppointment,
  fetchManagedAppointmentAvailability,
  rescheduleAppointment,
  updateAppointmentDetails,
} from '../api'
import { getGuestTimezone } from '../config'
import { BOOKING_CONTACT_LIMITS, normalizeBookingPhone } from '../lib/contact'
import {
  describeCalendarDate,
  describeDuration,
  describeLocation,
  formatLongDateTime,
  formatTime,
} from '../lib/format'
import type { AppointmentConfirmation, BookingAvailabilityResponse } from '../types'

type Mode = 'overview' | 'reschedule' | 'edit' | 'cancel'

/**
 * The guest's manage screen, reached only from the token in their confirmation
 * email. There is no account and no password — the token is the credential.
 */
export function ManagePage({ token }: { token: string }) {
  const guestTimezone = getGuestTimezone()

  const [appointment, setAppointment] = useState<AppointmentConfirmation | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('overview')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [availability, setAvailability] = useState<BookingAvailabilityResponse | null>(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestNotes, setGuestNotes] = useState('')
  const [cancelReason, setCancelReason] = useState('')

  useEffect(() => {
    let active = true
    fetchAppointment(token)
      .then((result) => {
        if (!active) return
        setAppointment(result)
        setGuestName(result.guestName)
        setGuestPhone(result.guestPhone ?? '')
        setGuestNotes(result.guestNotes ?? '')
      })
      .catch((cause: unknown) => {
        if (!active) return
        setLoadError(
          cause instanceof BookingApiError && cause.status === 404
            ? 'invalid'
            : cause instanceof Error ? cause.message : 'Something went wrong.',
        )
      })
    return () => {
      active = false
    }
  }, [token])

  /**
   * Rescheduling re-queries open slots for this session type. The token-scoped
   * endpoint leaves the guest's current appointment out of busy time, so their
   * own slot (and the buffers around it) stay selectable.
   */
  const loadAvailability = useCallback(() => {
    if (!appointment) return
    setAvailabilityLoading(true)
    fetchManagedAppointmentAvailability({ token })
      .then((result) => {
        setAvailability(result)
        setSelectedDate(result.days[0]?.date ?? null)
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Could not load available times.')
      })
      .finally(() => setAvailabilityLoading(false))
  }, [appointment, token])

  const applyResult = (result: AppointmentConfirmation, message: string) => {
    setAppointment(result)
    setNotice(message)
    setError(null)
    setMode('overview')
  }

  const handleReschedule = async (start: string) => {
    setBusy(true)
    setError(null)
    try {
      applyResult(
        await rescheduleAppointment(token, { start }),
        'Your appointment has been moved. A new confirmation is on its way.',
      )
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not move that appointment.')
    } finally {
      setBusy(false)
    }
  }

  const handleSaveDetails = async () => {
    const phone = normalizeBookingPhone(guestPhone)
    if (!phone.ok) {
      setError(phone.error)
      return
    }
    setBusy(true)
    setError(null)
    try {
      applyResult(
        await updateAppointmentDetails(token, {
          guestName: guestName.trim() || undefined,
          guestPhone: phone.value ?? '',
          guestNotes: guestNotes.trim(),
        }),
        'Your details have been updated.',
      )
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save those changes.')
    } finally {
      setBusy(false)
    }
  }

  const handleCancel = async () => {
    setBusy(true)
    setError(null)
    try {
      applyResult(
        await cancelAppointment(token, { reason: cancelReason.trim() || undefined }),
        'Your appointment has been cancelled.',
      )
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not cancel that appointment.')
    } finally {
      setBusy(false)
    }
  }

  if (loadError === 'invalid') {
    return (
      <CenteredState
        title="This link isn’t valid"
        description="It may have expired, or the appointment may already have been cancelled. Please use the most recent email from your practice, or contact them directly."
      />
    )
  }

  if (loadError) {
    return <CenteredState title="Something went wrong" description={loadError} />
  }

  if (!appointment) {
    return <CenteredState title="Loading" description="Finding your appointment…" loading />
  }

  const cancelled = appointment.status === 'cancelled'
  const summaryRows: SummaryRow[] = [
    { label: 'Session', value: appointment.appointmentTypeName },
    { label: 'Length', value: describeDuration(appointment.durationMinutes) },
    { label: 'When', value: formatLongDateTime(appointment.start, guestTimezone) },
    {
      label: 'Where',
      value: appointment.meetingUrl && !cancelled
        ? (
          <a className="booking-meet-link" href={appointment.meetingUrl} target="_blank" rel="noreferrer">
            Join on Google Meet
          </a>
        )
        : describeLocation(appointment.locationKind),
    },
    { label: 'Booked as', value: appointment.guestName },
  ]

  return (
    <BookingShell
      progress={100}
      practitionerName={appointment.practitionerName}
      organizationName={appointment.organizationName}
      summaryRows={summaryRows}
      statusPill={{
        label: cancelled ? 'Cancelled' : 'Confirmed',
        status: cancelled ? 'cancelled' : 'confirmed',
      }}
      onBack={mode === 'overview' ? undefined : () => setMode('overview')}
    >
      {mode === 'overview' && (
        <>
          <h1 className="booking-title">
            {cancelled ? 'This appointment was cancelled' : 'Your appointment'}
          </h1>
          <p className="booking-description">
            {cancelled
              ? 'Nothing further is needed. You’re welcome to book a new time whenever you’re ready.'
              : `${formatLongDateTime(appointment.start, guestTimezone)} with ${appointment.practitionerName}.`}
          </p>

          {!cancelled && appointment.canReschedule && (
            <>
              <button
                type="button"
                className="booking-primary"
                onClick={() => {
                  setMode('reschedule')
                  loadAvailability()
                }}
              >
                Reschedule
              </button>
              <button
                type="button"
                className="booking-secondary"
                onClick={() => setMode('edit')}
              >
                Update my details
              </button>
            </>
          )}

          {!cancelled && !appointment.canReschedule && (
            <div className="booking-notice">
              This appointment is close enough that changes need to go through the practice
              directly. Please get in touch with them.
            </div>
          )}

          {!cancelled && appointment.canCancel && (
            <button
              type="button"
              className="booking-secondary booking-danger"
              onClick={() => setMode('cancel')}
            >
              Cancel appointment
            </button>
          )}

          {cancelled && (
            <a
              className="booking-primary"
              href={`/book/${appointment.bookingSlug}`}
              style={{ textDecoration: 'none' }}
            >
              Book a new time
            </a>
          )}
        </>
      )}

      {mode === 'reschedule' && (
        <>
          <h1 className="booking-title">Pick a new time</h1>
          <p className="booking-description">
            Your current time is {formatLongDateTime(appointment.start, guestTimezone)}.
          </p>

          {availabilityLoading && (
            <div style={{ display: 'grid', gap: 8 }}>
              {[0, 1, 2, 3].map((index) => <span className="booking-skeleton" key={index} />)}
            </div>
          )}

          {!availabilityLoading && availability && (
            <>
              <div className="booking-date-strip">
                {availability.days.map((day) => {
                  const parts = describeCalendarDate(day.date)
                  return (
                    <button
                      key={day.date}
                      type="button"
                      className="booking-date"
                      data-selected={day.date === selectedDate}
                      onClick={() => setSelectedDate(day.date)}
                    >
                      <span className="booking-date-weekday">{parts.weekday}</span>
                      <span className="booking-date-day">{parts.day}</span>
                      <span className="booking-date-month">{parts.month}</span>
                    </button>
                  )
                })}
              </div>

              <div className="booking-slot-grid">
                {availability.days
                  .find((day) => day.date === selectedDate)
                  ?.slots.map((slot) => (
                    <button
                      key={slot.start}
                      type="button"
                      className="booking-slot"
                      disabled={busy}
                      onClick={() => void handleReschedule(slot.start)}
                    >
                      {formatTime(slot.start, guestTimezone)}
                    </button>
                  ))}
              </div>
            </>
          )}
        </>
      )}

      {mode === 'edit' && (
        <>
          <h1 className="booking-title">Update your details</h1>
          <p className="booking-description">
            Your practitioner sees these before the session.
          </p>

          <div className="booking-field-stack">
            <div className="booking-field">
              <label htmlFor="manage-name">Your name</label>
              <input
                id="manage-name"
                value={guestName}
                onChange={(event) => setGuestName(event.target.value)}
                maxLength={BOOKING_CONTACT_LIMITS.name}
              />
            </div>
            <div className="booking-field">
              <label htmlFor="manage-phone">Phone</label>
              <input
                id="manage-phone"
                type="tel"
                value={guestPhone}
                onChange={(event) => setGuestPhone(event.target.value)}
                placeholder="+1 514 555 0134"
                maxLength={BOOKING_CONTACT_LIMITS.phone}
              />
            </div>
            <div className="booking-field">
              <label htmlFor="manage-notes">Notes for your practitioner</label>
              <textarea
                id="manage-notes"
                value={guestNotes}
                onChange={(event) => setGuestNotes(event.target.value)}
                maxLength={BOOKING_CONTACT_LIMITS.notes}
              />
            </div>
          </div>

          <button
            type="button"
            className="booking-primary"
            disabled={busy}
            onClick={() => void handleSaveDetails()}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      )}

      {mode === 'cancel' && (
        <>
          <h1 className="booking-title">Cancel this appointment?</h1>
          <p className="booking-description">
            {formatLongDateTime(appointment.start, guestTimezone)} with{' '}
            {appointment.practitionerName}. Letting them know why is optional but appreciated.
          </p>

          <div className="booking-field">
            <label htmlFor="cancel-reason">Reason (optional)</label>
            <textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder="Something came up…"
            />
          </div>

          <button
            type="button"
            className="booking-primary"
            disabled={busy}
            onClick={() => void handleCancel()}
          >
            {busy ? 'Cancelling…' : 'Cancel my appointment'}
          </button>
          <button type="button" className="booking-secondary" onClick={() => setMode('overview')}>
            Keep my appointment
          </button>
        </>
      )}

      {notice && <div className="booking-notice">{notice}</div>}
      {error && <div className="booking-error">{error}</div>}
    </BookingShell>
  )
}
